import { useContext } from 'react';
import { AuthZeroApolloContext } from '@src/views/components/util/external/AuthZeroApolloClient';
import { ENVIRONMENT_IS_DEVELOPMENT, RESEARCH_API_BASE_URL } from '@src/constants';

export interface TngAgentPromptRequest {
    agentId: string;
    modelId: string;
    modelIdentifier: string;
    prompt: string;
    /** Existing dbo.AIConversation id; omit to start a new conversation. */
    conversationId?: string;
    documentIds?: string[];
}

export interface TngAgentGeneratedDocument {
    name: string;
    contentBase64: string;
    contentType: string;
}

export interface TngAgentPromptResult {
    response: string;
    conversationId: string;
    /** Only set when this call started a new conversation (summarized title). */
    conversationName?: string;
    /** dbo.AIConversationMessage id of the exchange this call saved. */
    messageId: string;
    documents: TngAgentGeneratedDocument[];
}

export interface AIConversationSummary {
    id: string;
    name: string;
    /** ISO date-time string */
    updatedUtc: string;
    isShared: boolean;
}

/** A shared conversation for a client; may belong to any account and any of the client's agents. */
export interface SharedAIConversationSummary extends AIConversationSummary {
    agentId: string;
    agentName?: string;
    /** dbo.Account id of the conversation's owner */
    accountId: string;
    ownerName?: string;
}

export interface AIConversationMessage {
    id: string;
    sequenceNumber: number;
    userPrompt: string;
    aiResponse: string;
    isError: boolean;
    errorMessage?: string;
    /** ISO date-time string */
    createdUtc: string;
    isShared: boolean;
}

export interface AIConversationSharingRequest {
    isShared: boolean;
    /** Messages to share; every other message in the conversation is unshared. Ignored when isShared is false. */
    sharedMessageIds: string[];
}

// Deployed URL comes from researchApiURL in src/environments/base.js.
// Local uvicorn runs plain HTTP, so this is hardcoded to http:// even when the frontend dev server is HTTPS.
const RESEARCH_API_URL = ENVIRONMENT_IS_DEVELOPMENT
    ? `http://${ window.location.hostname }:8000/`
    : RESEARCH_API_BASE_URL;

export const useResearchApi = () => {
    const { currentToken } = useContext( AuthZeroApolloContext );

    const authedFetch = ( path: string, init: RequestInit = {} ) =>
        fetch( `${ RESEARCH_API_URL }${ path }`, {
            ...init,
            headers: {
                ...( init.body ? { 'Content-Type': 'application/json' } : {} ),
                'Authorization': `Bearer ${ currentToken }`,
                ...init.headers,
            },
        } );

    const sendTngAgentPrompt = async ( request: TngAgentPromptRequest ): Promise<TngAgentPromptResult> => {
        const response = await authedFetch( 'tng-agent-prompt', {
            method: 'POST',
            body:   JSON.stringify( {
                agent_id:         request.agentId,
                model_id:         request.modelId,
                model_identifier: request.modelIdentifier,
                prompt:           request.prompt,
                conversation_id:  request.conversationId ?? null,
                document_ids:     request.documentIds ?? [],
            } ),
        } );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `TNG agent prompt failed (${ response.status }): ${ detail }` );
        }

        const data = await response.json();

        return {
            response:         data.response,
            conversationId:   data.conversation_id,
            conversationName: data.conversation_name ?? undefined,
            messageId:        data.message_id,
            documents:        ( data.documents ?? [] ).map( ( doc: any ) => ( {
                name:          doc.name,
                contentBase64: doc.content_base64,
                contentType:   doc.content_type,
            } ) ),
        };
    };

    const listAIConversations = async ( agentId: string ): Promise<AIConversationSummary[]> => {
        const response = await authedFetch( `ai-conversations?agent_id=${ agentId }` );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `Failed to load conversations (${ response.status }): ${ detail }` );
        }

        const data = await response.json();

        return ( data ?? [] ).map( ( row: any ) => ( {
            id:         row.id,
            name:       row.name,
            updatedUtc: row.updated_utc,
            isShared:   !!row.is_shared,
        } ) );
    };

    const listSharedAIConversations = async ( clientId: string ): Promise<SharedAIConversationSummary[]> => {
        const response = await authedFetch( `ai-conversations/shared?client_id=${ clientId }` );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `Failed to load shared conversations (${ response.status }): ${ detail }` );
        }

        const data = await response.json();

        return ( data ?? [] ).map( ( row: any ) => ( {
            id:         row.id,
            name:       row.name,
            updatedUtc: row.updated_utc,
            isShared:   true,
            agentId:    row.agent_id,
            agentName:  row.agent_name ?? undefined,
            accountId:  row.account_id,
            ownerName:  row.owner_name ?? undefined,
        } ) );
    };

    /**
     * Copies a conversation (only its shared messages, unless you own it) into a new unshared conversation
     * owned by the current user, continued with agentId.
     */
    const copyAIConversation = async ( conversationId: string, agentId: string ): Promise<AIConversationSummary & { agentId: string }> => {
        const response = await authedFetch( `ai-conversations/${ conversationId }/copy`, {
            method: 'POST',
            body:   JSON.stringify( { agent_id: agentId } ),
        } );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `Failed to copy conversation (${ response.status }): ${ detail }` );
        }

        const row = await response.json();

        return {
            id:         row.id,
            name:       row.name,
            updatedUtc: row.updated_utc,
            isShared:   false,
            agentId:    row.agent_id,
        };
    };

    /** The owner gets every message; any other account only gets the messages marked shared. */
    const listAIConversationMessages = async ( conversationId: string ): Promise<AIConversationMessage[]> => {
        const response = await authedFetch( `ai-conversations/${ conversationId }/messages` );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `Failed to load conversation messages (${ response.status }): ${ detail }` );
        }

        const data = await response.json();

        return ( data ?? [] ).map( ( row: any ) => ( {
            id:             row.id,
            sequenceNumber: row.sequence_number,
            userPrompt:     row.user_prompt,
            aiResponse:     row.ai_response,
            isError:        row.is_error,
            errorMessage:   row.error_message ?? undefined,
            createdUtc:     row.created_utc,
            isShared:       !!row.is_shared,
        } ) );
    };

    const updateAIConversationSharing = async ( conversationId: string, request: AIConversationSharingRequest ): Promise<void> => {
        const response = await authedFetch( `ai-conversations/${ conversationId }/sharing`, {
            method: 'PUT',
            body:   JSON.stringify( {
                is_shared:          request.isShared,
                shared_message_ids: request.isShared ? request.sharedMessageIds : [],
            } ),
        } );

        if( !response.ok ){
            const detail = await response.text().catch( () => '' );
            throw new Error( `Failed to update conversation sharing (${ response.status }): ${ detail }` );
        }
    };

    return { sendTngAgentPrompt, listAIConversations, listSharedAIConversations, listAIConversationMessages, copyAIConversation, updateAIConversationSharing };
};
